import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  CreditCard,
  Droplets,
  Factory,
  Flame,
  Menu,
  Gauge,
  GitCompareArrows,
  Lock,
  LogOut,
  FileCheck2,
  FileText,
  Settings,
  ShieldCheck,
  Smartphone,
  UserRound,
  UsersRound,
  Warehouse,
  Waves,
  Wrench,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { languageRootPath } from '@app/i18n/languages';
import { LanguageSwitcher } from '@shared/components/LanguageSwitcher';
import { OfflineBanner } from '@shared/offline/OfflineBanner';
import { SyncStatusPanel } from '@features/sync/components/SyncStatusPanel';
import { useSyncOnReconnect } from '@features/sync/hooks/useSyncOnReconnect';
import { StationSelector } from '@features/stations/components/StationSelector';
import { useAuth } from '@/hooks/useAuth';
import { canManageTeam } from '@/lib/roles';
import { env } from '@/config/env';
import { BillingStatusBanner } from '@features/billing/components/BillingStatusBanner';
import { BillingGateBanner } from '@features/billing/components/BillingGateBanner';
import { useBillingGate, useBillingGateSync } from '@features/billing/hooks/useBillingGate';
import { UpdateAvailableBanner } from '@features/appVersion/components/UpdateAvailableBanner';
import { MaintenanceBanner } from '@features/appVersion/components/MaintenanceBanner';
import { useMaintenanceState } from '@features/appVersion/hooks/useMaintenanceState';
import { useSelectedStation } from '@features/stations/hooks/useSelectedStation';
import { SetupGuideLauncher } from '@features/onboarding/components/SetupGuideLauncher';

type SidebarLink = {
  type: 'link';
  to: string;
  icon: typeof Gauge;
  labelKey: string;
  end?: boolean;
  /** Write action — disabled when the org is in billing read-only mode (D1). */
  requiresWrite?: boolean;
};

type SidebarGroup = {
  type: 'group';
  id: string;
  icon: typeof Gauge;
  labelKey: string;
  children: SidebarLink[];
};

type SidebarItem = SidebarLink | SidebarGroup;

const buildNavigation = (selectedStationId?: string, canManageTeam = false): SidebarItem[] => [
  { type: 'link', to: 'dashboard', icon: Gauge, labelKey: 'nav.dashboard', end: true },
  { type: 'link', to: 'compliance/calendar', icon: CalendarDays, labelKey: 'nav.calendar' },
  {
    type: 'group',
    id: 'fuel-receptions',
    icon: Droplets,
    labelKey: 'nav.fuelReceptions',
    children: [
      {
        type: 'link',
        to: 'fuel-receptions',
        icon: SearchIcon,
        labelKey: 'nav.fuelReceptionSearch',
        end: true
      },
      {
        type: 'link',
        to: 'fuel-receptions/new',
        icon: Droplets,
        labelKey: 'nav.fuelReceptionNew',
        requiresWrite: true
      },
      {
        type: 'link',
        to: 'reports/official',
        icon: FileText,
        labelKey: 'nav.auditReport'
      }
    ]
  },
  {
    type: 'group',
    id: 'incidents',
    icon: AlertTriangle,
    labelKey: 'nav.incidents',
    children: [
      {
        type: 'link',
        to: 'incidents',
        icon: SearchIcon,
        labelKey: 'nav.incidentSearch',
        end: true
      },
      {
        type: 'link',
        to: 'incidents/new',
        icon: AlertTriangle,
        labelKey: 'nav.incidentNew',
        requiresWrite: true
      },
      {
        type: 'link',
        to: 'reports/incidents',
        icon: FileText,
        labelKey: 'nav.incidentReport'
      }
    ]
  },
  {
    type: 'group',
    id: 'dangerous-work',
    icon: Flame,
    labelKey: 'nav.dangerousWork',
    children: [
      {
        type: 'link',
        to: 'dangerous-work',
        icon: SearchIcon,
        labelKey: 'nav.dangerousWorkSearch',
        end: true
      },
      {
        type: 'link',
        to: 'dangerous-work/new',
        icon: Flame,
        labelKey: 'nav.dangerousWorkNew',
        requiresWrite: true
      },
      {
        type: 'link',
        to: 'reports/dangerous-work',
        icon: FileText,
        labelKey: 'nav.dangerousWorkReports'
      }
    ]
  },
  {
    type: 'group',
    id: 'station-changes',
    icon: GitCompareArrows,
    labelKey: 'nav.stationChanges',
    children: [
      {
        type: 'link',
        to: 'station-changes',
        icon: SearchIcon,
        labelKey: 'nav.stationChangeSearch',
        end: true
      },
      {
        type: 'link',
        to: 'station-changes/new',
        icon: GitCompareArrows,
        labelKey: 'nav.stationChangeNew',
        requiresWrite: true
      },
      {
        type: 'link',
        to: 'reports/station-changes',
        icon: FileText,
        labelKey: 'nav.stationChangeReports'
      }
    ]
  },
  {
    type: 'group',
    id: 'well-monitoring',
    icon: Waves,
    labelKey: 'nav.wellMonitoring',
    children: [
      {
        type: 'link',
        to: 'monitoring/inspections',
        icon: ClipboardCheck,
        labelKey: 'nav.inspectionHistory',
        end: true
      },
      {
        type: 'link',
        to: 'monitoring/inspections/new',
        icon: ClipboardCheck,
        labelKey: 'nav.inspectionNew',
        requiresWrite: true
      },
      {
        type: 'link',
        to: 'reports/monitoring',
        icon: FileText,
        labelKey: 'nav.monitoringAuditReport'
      }
    ]
  },
  {
    type: 'group',
    id: 'maintenance',
    icon: Wrench,
    labelKey: 'nav.maintenance',
    children: [
      {
        type: 'link',
        to: 'maintenance/records',
        icon: SearchIcon,
        labelKey: 'nav.maintenanceSearch',
        end: true
      },
      {
        type: 'link',
        to: 'maintenance/records/new',
        icon: Wrench,
        labelKey: 'nav.maintenanceNew',
        requiresWrite: true
      },
      {
        type: 'link',
        to: 'maintenance/reports',
        icon: FileCheck2,
        labelKey: 'nav.maintenanceReports'
      }
    ]
  },
  {
    type: 'group',
    id: 'station',
    icon: Factory,
    labelKey: 'nav.station',
    children: [
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/compliance-profile` : 'dashboard',
        icon: Building2,
        labelKey: 'nav.companyData'
      },
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/areas` : 'dashboard',
        icon: Warehouse,
        labelKey: 'nav.areas'
      },
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/tanks` : 'dashboard',
        icon: Droplets,
        labelKey: 'nav.tanks'
      },
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/dispensers` : 'dashboard',
        icon: Gauge,
        labelKey: 'nav.dispensers'
      },
      {
        type: 'link',
        to: 'monitoring/wells',
        icon: Waves,
        labelKey: 'nav.wells'
      }
    ]
  },
  ...(env.billingEnabled
    ? [{ type: 'link' as const, to: 'billing', icon: CreditCard, labelKey: 'nav.billing' }]
    : []),
  {
    type: 'group',
    id: 'settings',
    icon: Settings,
    labelKey: 'nav.settings',
    children: [
      ...(canManageTeam
        ? [
            {
              type: 'link' as const,
              to: 'settings/users',
              icon: UsersRound,
              labelKey: 'nav.users'
            }
          ]
        : []),
      {
        type: 'link',
        to: 'settings/profile',
        icon: UserRound,
        labelKey: 'nav.profile'
      },
      {
        type: 'link',
        to: 'settings/sessions',
        icon: Smartphone,
        labelKey: 'nav.sessions'
      },
      {
        type: 'link',
        to: 'settings/privacy',
        icon: ShieldCheck,
        labelKey: 'nav.privacy'
      }
    ]
  }
];

const SearchIcon = Activity;

export const AppLayout = () => {
  const { t } = useTranslation();
  const { lng = 'es' } = useParams();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const maintenance = useMaintenanceState();
  const { selectedStationId } = useSelectedStation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isReadOnly } = useBillingGate();
  useBillingGateSync();
  useSyncOnReconnect();

  const handleLogout = async () => {
    // Navigate to the language root FIRST, then sign out. Leaving the protected
    // subtree before the session clears stops ProtectedRoute from bouncing us to
    // /login the instant `user` becomes null (the landing is public).
    void navigate(languageRootPath(lng), { replace: true });
    await logout();
  };

  return (
    <div className="min-h-screen bg-slate-50 text-ink">
      <OfflineBanner />
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-72 border-r border-slate-800 bg-shell text-white lg:block">
        <SidebarContent
          lng={lng}
          selectedStationId={selectedStationId}
          readOnly={isReadOnly}
          onNavigate={() => undefined}
        />
      </aside>

      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <button
            className="absolute inset-0 bg-slate-950/55"
            type="button"
            aria-label={t('nav.closeMenu')}
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="relative flex h-full w-[min(86vw,20rem)] flex-col border-r border-slate-800 bg-shell text-white shadow-2xl">
            <div className="flex h-16 items-center justify-between border-b border-white/10 pr-3">
              <SidebarBrand />
              <button
                className="grid h-10 w-10 place-items-center rounded-md text-slate-200 hover:bg-white/10"
                type="button"
                aria-label={t('nav.closeMenu')}
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <SidebarNav
              lng={lng}
              selectedStationId={selectedStationId}
              readOnly={isReadOnly}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </aside>
        </div>
      ) : null}

      <div className="lg:pl-72">
        <header className="sticky top-0 z-10 border-b border-line bg-white/95 backdrop-blur">
          <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <button
                className="toolbar-button lg:hidden"
                type="button"
                aria-label={t('nav.openMenu')}
                title={t('nav.openMenu')}
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="h-4 w-4" aria-hidden="true" />
              </button>
              <Activity className="h-5 w-5 text-brand-600" aria-hidden="true" />
              <span className="hidden text-sm font-semibold sm:inline">{t('app.workspace')}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <StationSelector />
              {user ? (
                <Link
                  to={`/${lng}/settings/profile`}
                  className="hidden max-w-[12rem] truncate text-sm text-muted hover:text-ink md:inline"
                  title={t('nav.profile')}
                >
                  {user.full_name?.trim() || user.email}
                </Link>
              ) : null}
              <SyncStatusPanel compact />
              <LanguageSwitcher />
              <button
                className="toolbar-button"
                type="button"
                aria-label={t('auth.logout')}
                title={t('auth.logout')}
                onClick={() => void handleLogout()}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>
        {maintenance ? (
          <MaintenanceBanner
            level={maintenance.level}
            retryAfterSeconds={maintenance.retryAfterSeconds}
          />
        ) : null}
        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-4 space-y-3">
            <UpdateAvailableBanner />
            <BillingGateBanner />
            <BillingStatusBanner />
          </div>
          <Outlet />
        </main>
        <footer className="border-t border-line px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-2 text-xs text-muted sm:flex-row">
            <span>© {new Date().getFullYear()} LogBooks</span>
            <nav className="flex items-center gap-3">
              <Link
                to={`/${lng}/legal/privacy`}
                className="underline underline-offset-2 hover:text-ink"
              >
                {t('legal.privacyTitle')}
              </Link>
              <span aria-hidden="true">·</span>
              <Link
                to={`/${lng}/legal/terms`}
                className="underline underline-offset-2 hover:text-ink"
              >
                {t('legal.termsTitle')}
              </Link>
            </nav>
          </div>
        </footer>
      </div>

      <SetupGuideLauncher />
    </div>
  );
};

const SidebarBrand = () => {
  const { t } = useTranslation();

  return (
    <div className="flex h-16 items-center gap-3 px-5">
      <div className="grid h-9 w-9 place-items-center rounded-md bg-brand-500">
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />
      </div>
      <div>
        <p className="text-sm font-semibold">LogBooks</p>
        <p className="text-xs text-slate-300">{t('app.tagline')}</p>
      </div>
    </div>
  );
};

const SidebarNav = ({
  lng,
  selectedStationId,
  readOnly,
  onNavigate
}: {
  lng: string;
  selectedStationId?: string;
  readOnly?: boolean;
  onNavigate: () => void;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const items = useMemo(
    () => buildNavigation(selectedStationId, canManageTeam(user?.role)),
    [selectedStationId, user?.role]
  );
  const activeGroupIds = useMemo(
    () =>
      items
        .filter((item): item is SidebarGroup => item.type === 'group')
        .filter((item) =>
          item.children.some((child) => location.pathname.startsWith(`/${lng}/${child.to}`))
        )
        .map((item) => item.id),
    [items, lng, location.pathname]
  );
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(activeGroupIds));

  useEffect(() => {
    if (activeGroupIds.length === 0) {
      return;
    }

    setOpenGroups((current) => {
      const next = new Set(current);
      activeGroupIds.forEach((id) => next.add(id));
      return next;
    });
  }, [activeGroupIds]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups((current) => {
      const next = new Set(current);

      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }

      return next;
    });
  };

  return (
    <nav className="space-y-1 px-3 py-4">
      {items.map((item) => {
        if (item.type === 'link') {
          return (
            <SidebarNavLink
              key={item.to}
              item={item}
              lng={lng}
              readOnly={readOnly}
              onNavigate={onNavigate}
            />
          );
        }

        const Icon = item.icon;
        const isOpen = openGroups.has(item.id);
        const isActive = activeGroupIds.includes(item.id);

        return (
          <div key={item.id}>
            <button
              type="button"
              className={[
                'flex min-h-11 w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition',
                isActive
                  ? 'bg-white/12 text-white'
                  : 'text-slate-300 hover:bg-white/8 hover:text-white'
              ].join(' ')}
              aria-expanded={isOpen}
              onClick={() => toggleGroup(item.id)}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span className="min-w-0 flex-1">{t(item.labelKey)}</span>
              <ChevronDown
                className={[
                  'h-4 w-4 transition-transform',
                  isOpen ? 'rotate-180 text-white' : 'text-slate-400'
                ].join(' ')}
                aria-hidden="true"
              />
            </button>
            {isOpen ? (
              <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
                {item.children.map((child) => (
                  <SidebarNavLink
                    key={`${item.id}-${child.to}-${child.labelKey}`}
                    item={child}
                    lng={lng}
                    readOnly={readOnly}
                    onNavigate={onNavigate}
                    nested
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
};

const SidebarNavLink = ({
  item,
  lng,
  readOnly,
  onNavigate,
  nested
}: {
  item: SidebarLink;
  lng: string;
  readOnly?: boolean;
  onNavigate: () => void;
  nested?: boolean;
}) => {
  const { t } = useTranslation();
  const Icon = item.icon;

  if (readOnly && item.requiresWrite) {
    return (
      <span
        aria-disabled="true"
        title={t('billing.gate.writeDisabled')}
        className={[
          'flex min-h-10 cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-slate-500 opacity-60',
          nested ? 'text-xs' : 'text-sm'
        ].join(' ')}
      >
        <Icon className={nested ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
        {t(item.labelKey)}
        <Lock className="ml-auto h-3.5 w-3.5" aria-hidden="true" />
      </span>
    );
  }

  return (
    <NavLink
      to={`/${lng}/${item.to}`}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm transition',
          nested ? 'text-xs' : 'text-sm',
          isActive ? 'bg-white/12 text-white' : 'text-slate-300 hover:bg-white/8 hover:text-white'
        ].join(' ')
      }
    >
      <Icon className={nested ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
      {t(item.labelKey)}
    </NavLink>
  );
};

const SidebarContent = ({
  lng,
  selectedStationId,
  readOnly,
  onNavigate
}: {
  lng: string;
  selectedStationId?: string;
  readOnly?: boolean;
  onNavigate: () => void;
}) => (
  <>
    <div className="border-b border-white/10">
      <SidebarBrand />
    </div>
    <SidebarNav
      lng={lng}
      selectedStationId={selectedStationId}
      readOnly={readOnly}
      onNavigate={onNavigate}
    />
  </>
);
